import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getTerminologyItem, updateTerminologyItem } from "@/api/terminology";
import type { TerminologyItem } from "@/api/terminology";
import { useToast } from "@/components/toast";

import { SenseSearch } from "../SenseSearch";
import type { SenseTarget } from "../senseSearchTypes";

const TRUSTED = new Set(["human", "regulatory_aligned", "expert_reviewed", ""]);

function isHard(item: TerminologyItem): boolean {
  const ext = (item.external_ids ?? {}) as Record<string, unknown>;
  return (
    ext.constraint_eligible !== false &&
    ext.morph_safe !== false &&
    TRUSTED.has(String(ext.ru_term_source ?? ""))
  );
}

export function HardTranslationPanel() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<SenseTarget | null>(null);

  const detail = useQuery({
    queryKey: ["terminology-item", picked?.item_id],
    queryFn: () => getTerminologyItem(picked!.item_id),
    enabled: !!picked,
  });
  const item = detail.data?.ok ? detail.data.data.item : null;
  const hard = item ? isHard(item) : false;

  const mut = useMutation({
    mutationFn: async (on: boolean) => {
      const ext: Record<string, unknown> = { ...(item?.external_ids ?? {}) };
      if (on) {
        ext.constraint_eligible = true;
        ext.morph_safe = true;
        ext.ru_term_source = "human";
      } else {
        ext.constraint_eligible = false;
      }
      const r = await updateTerminologyItem(picked!.item_id, {
        external_ids: ext,
      });
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: () => {
      toast("Готово", "success");
      void qc.invalidateQueries({
        queryKey: ["terminology-item", picked?.item_id],
      });
    },
    onError: (e: Error) => toast(e.message, "error"),
  });

  return (
    <div>
      <SenseSearch
        allow={["glossary_term", "abbreviation"]}
        placeholder="Найти термин или аббревиатуру (RU/EN)…"
        onPick={setPicked}
        pickedItemId={picked?.item_id}
      />
      {picked && detail.isLoading && (
        <div className="mt-muted" style={{ marginTop: 12, fontSize: "var(--fs-sm)" }}>
          Загружаю…
        </div>
      )}
      {picked && item && item.item_type === "abbreviation" && (
        <div
          className="rf-example"
          style={{ marginTop: 12, padding: 8, fontSize: "var(--fs-sm)" }}
        >
          <b>
            {item.source_text} →{" "}
            {item.target_abbreviation || item.target_text}
          </b>{" "}
          — это аббревиатура, уже одобренная в словаре. Поэтому целевая форма и
          подставляется всегда: у аббревиатур нет режима «подсказка» — одобренная
          запись применяется жёстко сама по себе. Отдельной кнопки «зафиксировать»
          тут не нужно (она — для обычных терминов). Если у аббревиатуры несколько
          смыслов — открой «развести смыслы аббревиатуры».
          <div style={{ marginTop: 8 }}>
            <Link
              className="mt-btn mt-btn--sm mt-btn--ghost"
              to={`/admin/terminology/${encodeURIComponent(picked.item_id)}`}
            >
              открыть карточку для правки →
            </Link>
          </div>
        </div>
      )}
      {picked && item && item.item_type !== "abbreviation" && (
        <div style={{ marginTop: 12 }}>
          <div className="mt-muted" style={{ marginBottom: 8 }}>
            Перевод: <b>{item.target_text || "—"}</b>{" "}
            <span
              className={
                "mt-badge mt-badge--no-dot " +
                (hard ? "mt-badge--success" : "mt-badge--info")
              }
            >
              {hard ? "сейчас: жёстко" : "сейчас: подсказка"}
            </span>
          </div>
          {hard ? (
            <button
              type="button"
              className="mt-btn mt-btn--sm mt-btn--ghost"
              disabled={mut.isPending}
              onClick={() => mut.mutate(false)}
            >
              Сделать только подсказкой
            </button>
          ) : (
            <button
              type="button"
              className="mt-btn mt-btn--sm mt-btn--primary"
              disabled={mut.isPending}
              onClick={() => mut.mutate(true)}
            >
              Зафиксировать жёстко
            </button>
          )}
        </div>
      )}
    </div>
  );
}
